import React, { useState, useEffect } from 'react';

import { Add } from '@mui/icons-material';
import { Tooltip, Fab } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  FeatureSource,
  ModelBase,
} from "@models";
import { 
  GameClass, 
  UpgradableNumber
} from "@dndModels";
import { 
  ABILITY_SCORES,
  CLASS_GROUPS
} from "@dndModels/Constants";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import FeatureSourceInput from "@dndComponents/FeatureSourceInput";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import EditableLabel from "@components/input/strings/EditableLabel";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import StringBox from "@components/input/strings/StringBox";
import CheckBox from "@components/input/CheckBox";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  obj?: ModelBase;
}

export default function GameClassEdit({ obj: param_obj }: Props) {
  const [reloading, setReloading] = useState(false);
  const [obj, setObj] = useState<GameClass>(param_obj instanceof GameClass ? param_obj : api.applyWorkingOn(new GameClass()));
  const [change_time, setChangeTime] = useState(0);
  
  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  return (
    <FeatureSourceInput 
      obj={obj}
      data_type="game_class"
      disabled={disabled}
      change_time={change_time}
      onChange={(changed: FeatureSource) => {
        if (changed instanceof GameClass) {
          const new_obj = obj;
          new_obj.copy(changed);
          setObj(new_obj);
        }
      }}
      extra_menu_options={{"Attributes": 
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          <Grid
            size={{
              xs: 12
            }}>
            <CheckBox
              name="Sidekick"
              value={obj.sidekick}
              disabled={disabled}
              onChange={(changed: boolean) => {
                const new_obj = obj;
                new_obj.sidekick = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox 
              name="Primary Ability"
              options={ABILITY_SCORES}
              values={obj.primary_abilities} 
              disabled={ disabled }
              multiple
              onChange={(changed: string[]) => {
                const new_obj = obj;
                new_obj.primary_abilities = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox 
              name="Secondary Ability"
              options={ABILITY_SCORES}
              values={obj.secondary_abilities} 
              disabled={ disabled }
              multiple
              onChange={(changed: string[]) => {
                const new_obj = obj;
                new_obj.secondary_abilities = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox 
              name="Hit Die"
              options={["d6","d8","d10","d12"]}
              value={`d${obj.hit_die}`} 
              disabled={ disabled }
              onChange={(changed: string) => {
                const new_obj = obj;
                new_obj.hit_die = +(changed.substring(1));
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox  
              name="Subclass Level"
              options={["1","2","3"]}
              value={`${obj.subclass_level}`}
              disabled={ disabled }
              onChange={(changed: string) => {
                const new_obj = obj;
                new_obj.subclass_level = +changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox 
              name="Subclasses Called"
              value={obj.subclasses_called} 
              disabled={ disabled }
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.subclasses_called = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox 
              name="Subclass Description"
              value={obj.subclass_description} 
              multiline
              disabled={ disabled }
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.subclass_description = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox 
              name="Class Group"
              options={CLASS_GROUPS}
              value={obj.class_group} 
              disabled={ disabled }
              onChange={(changed: string) => {
                const new_obj = obj;
                new_obj.class_group = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <span className="header">
              Formulae
            </span>
            { !disabled &&
              <Tooltip title={`Create New Formula`}>
                <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                  onClick={ () => {
                    const new_obj = obj;
                    new_obj.formulae["New Formula"] = new UpgradableNumber();
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                    setReloading(true);
                  }}>
                  <Add/>
                </Fab>
              </Tooltip>
            }
          </Grid>
          { !reloading && Object.keys(obj.formulae).sort((a, b) => {
            return a.localeCompare(b);
          }).map((formula_key, key) => {
            return (
              <Grid
                key={key}
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                <Grid
                  size={{
                    xs: 2
                  }}>
                  <EditableLabel 
                    name="Formula Key"
                    value={formula_key}
                    onChange={(changed: string) => {
                      const new_obj = obj;
                      const old_changed = new_obj.formulae[formula_key].clone();
                      delete new_obj.formulae[formula_key];
                      new_obj.formulae[changed] = old_changed;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                      setReloading(true);
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 10
                  }}>
                  <UpgradableNumberBox 
                    value={ obj.formulae[formula_key] as UpgradableNumber } 
                    name={ formula_key }
                    disabled={disabled}
                    onChange={(changed: UpgradableNumber) => {
                      const new_obj = obj;
                      new_obj.formulae[formula_key] = changed;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      }}
    />
  ); 
}
